/**
 * SupportTicketsSection — "Deine Tickets": Liste eigener Support-Anfragen mit
 * aufklappbarem Thread + Antwortformular (nur solange das Ticket offen ist).
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSupportTickets, useSupportTicket, useReplySupportTicket } from './useSupportTickets'

function formatDate(value: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
}

function TicketThread({ ticketId }: { ticketId: number }) {
  const { t, i18n } = useTranslation('support')
  const { ticket, isLoading } = useSupportTicket(ticketId)
  const [message, setMessage] = useState('')
  const reply = useReplySupportTicket(ticketId)

  if (isLoading || !ticket) {
    return <p className="muted" style={{ fontSize: 13 }}>{t('loading')}</p>
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!message.trim()) return
    reply.mutate(message.trim(), { onSuccess: () => setMessage('') })
  }

  return (
    <div data-testid={`support-ticket-thread-${ticketId}`} style={{ padding: '12px 0' }}>
      {ticket.messages.map((m) => (
        <div
          key={m.id}
          className="card"
          style={{
            padding: 10,
            marginBottom: 8,
            background: m.sender === 'admin' ? 'var(--surface-muted, #f5f5f5)' : undefined,
          }}
        >
          <div className="muted" style={{ fontSize: 11, marginBottom: 4 }}>
            {t(m.sender === 'admin' ? 'tickets.sender.admin' : 'tickets.sender.user')}
            {' · '}
            {formatDate(m.created_at, i18n.language)}
          </div>
          <p style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 13 }}>{m.body}</p>
        </div>
      ))}

      {ticket.status === 'offen' ? (
        <form onSubmit={handleSubmit} style={{ marginTop: 8 }}>
          <textarea
            className="textarea"
            rows={3}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={5000}
            placeholder={t('tickets.replyPlaceholder')}
            disabled={reply.isPending}
            data-testid="support-ticket-reply-input"
          />
          <button
            type="submit"
            className="btn btn-sm btn-primary"
            disabled={reply.isPending || !message.trim()}
            style={{ marginTop: 8 }}
            data-testid="support-ticket-reply-submit"
          >
            {reply.isPending ? t('tickets.replying') : t('tickets.replySubmit')}
          </button>
        </form>
      ) : (
        <p className="muted" style={{ fontSize: 12 }}>{t('tickets.closed')}</p>
      )}
    </div>
  )
}

export function SupportTicketsSection() {
  const { t, i18n } = useTranslation('support')
  const { tickets, isLoading, isError } = useSupportTickets()
  const [openId, setOpenId] = useState<number | null>(null)

  return (
    <div className="card card-pad" data-testid="support-tickets-section">
      <h3 style={{ margin: '0 0 10px', fontSize: 14 }}>{t('tickets.title')}</h3>

      {isLoading && <p className="muted" style={{ fontSize: 13 }}>{t('loading')}</p>}
      {isError && <p className="muted" style={{ fontSize: 13 }}>{t('tickets.loadError')}</p>}
      {!isLoading && !isError && tickets && tickets.length === 0 && (
        <p className="muted" style={{ fontSize: 13 }} data-testid="support-tickets-empty">
          {t('tickets.empty')}
        </p>
      )}

      {tickets?.map((ticket) => (
        <div key={ticket.id} className="support-faq" data-testid={`support-ticket-row-${ticket.id}`}>
          <button
            type="button"
            onClick={() => setOpenId(openId === ticket.id ? null : ticket.id)}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px 0',
              textAlign: 'left',
            }}
          >
            <span>{ticket.subject}</span>
            <span
              className={ticket.status === 'offen' ? 'pill pill-success' : 'pill'}
              style={{ marginLeft: 8 }}
            >
              {t(`tickets.status.${ticket.status}`)}
            </span>
          </button>
          <div className="muted" style={{ fontSize: 11 }}>{formatDate(ticket.created_at, i18n.language)}</div>
          {openId === ticket.id && <TicketThread ticketId={ticket.id} />}
        </div>
      ))}
    </div>
  )
}
